import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { setParcelTemplate } from '../../../../entities/providers';
import * as selectors from '../../../../entities/providers/selectors';

import { Radio, Tag, Checkbox, Form } from '../../../element';
import { ElementLink } from '../../../anchor/link';

import { isInPostGB, ParcelTemplate } from '../../../../config/providers';

import { ParcelWeightSlider } from '../../../providers/parcel-weight-slider';
import { ParcelPrice } from '../../../providers/parcel-price';
import { ParcelWidthSlider } from '../../../providers/parcel-width-slider';
import { ParcelHeightSlider } from '../../../providers/parcel-height-slider';
import { ParcelLengthSlider } from '../../../providers/parcel-length-slider';

import { ParcelSliderContainer } from './styled';

import { useTranslation } from '../../../../i18n';

const ParcelParams: React.FC<{ parcelTemplates: ParcelTemplate[] }> = ({ parcelTemplates }) => {
  const [t] = useTranslation(['rates', 'common']);

  const dispatch = useDispatch();

  const provider = useSelector(selectors.getCurrentProvider);
  const template = useSelector(selectors.geParcelTemplate);
  const hasCustomSize = useSelector(selectors.hasServiceCustomSize);

  const handleChange = useCallback((parcelTemplate: string) => {
    dispatch(setParcelTemplate({ parcelTemplate }));
  }, []);

  const toggleCustomSize = useCallback(
    (checked: boolean) => {
      const tpl = checked ? '' : parcelTemplates[0].type;

      handleChange(tpl);
    },
    [parcelTemplates]
  );

  const showLocker: boolean = isInPostGB(provider);

  return (
    <>
      {template && (
        <>
          <Form.Item>
            <Radio.Group value={template.type} onChange={handleChange}>
              {parcelTemplates.map((size) => (
                <Radio.Button key={size.type} value={size.type}>
                  {size.label}
                </Radio.Button>
              ))}
            </Radio.Group>
          </Form.Item>

          <Form.Item>
            <Tag className="el-tag--info el-tag--light">
              {t(`rates.delivery.sizes.${template.type}`)} {template.width}x{template.height}x{template.length}{' '}
              {t('common:units.cm')} {t('rates.delivery.upTo')} {template.maxWeight} {t('common:units.kg')}
            </Tag>
          </Form.Item>
        </>
      )}

      {showLocker && (
        <div>
          <ElementLink
            href="https://www.inpost.co.uk/users/find-a-locker"
            target="_blank"
            className="el-button el-button--inpost"
          >
            Find a locker
          </ElementLink>
        </div>
      )}

      {hasCustomSize && (
        <Form.Item>
          <Checkbox checked={!template} onChange={toggleCustomSize}>
            {t('rates.delivery.parcelLargeLabel')}
          </Checkbox>
        </Form.Item>
      )}
    </>
  );
};

const ParcelSlider: React.FC = () => {
  const [t] = useTranslation(['rates', 'common']);
  const size = useSelector(selectors.getSize);

  return (
    <ParcelSliderContainer>
      <div className="el-form-item">
        <ParcelWidthSlider showTooltip />
      </div>

      <div className="el-form-item">
        <ParcelHeightSlider showTooltip />
      </div>

      <div className="el-form-item">
        <ParcelLengthSlider showTooltip />
      </div>

      <div className="el-form-item">
        <Tag className="el-tag--info el-tag--light">
          {t('rates.delivery.parcel.size')}: {size} {t('common:units.cm')}
        </Tag>
      </div>
    </ParcelSliderContainer>
  );
};

const WeightLabel = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: -10px;

  & b {
    font-size: 16px;
    font-weight: bold;
  }

  & span:before {
    content: '*';
    color: #f56c6c;
    margin-right: 4px;
  }
`;

export const ParcelType: React.FC = () => {
  const [t] = useTranslation(['rates', 'common']);

  const parcelTemplates = useSelector(selectors.geParcelTemplates);
  const template = useSelector(selectors.geParcelTemplate);
  const weight = useSelector(selectors.getParcelWeight);

  const hasTemplates = Array.isArray(parcelTemplates) && parcelTemplates.length > 0;
  const extendedFilters = Boolean(!template || !template.type);

  return (
    <>
      <h2>{t('rates.delivery.parcel.title')}</h2>

      {hasTemplates && <ParcelParams parcelTemplates={parcelTemplates} />}

      <ParcelSliderContainer>
        <Form.Item>
          <WeightLabel>
            <span>
              {t('rates.delivery.weight')} ({t('common:units.kg')})
            </span>
            <b>
              {weight.toFixed(3)} {t('common:units.kg')}
            </b>
          </WeightLabel>

          <ParcelWeightSlider showTooltip />
        </Form.Item>
      </ParcelSliderContainer>

      <Form.Item>
        <ParcelPrice />
      </Form.Item>

      {extendedFilters && <ParcelSlider />}
    </>
  );
};
